import styles from './instructor.module.scss';
import { CreateHeaders, ErrorButton, HeaderSizeEnum, Icon, PrimaryButton, textHeader } from '@frontend-workspace/react-shared-lib';
import { ValueFormatterParams } from 'ag-grid-community';
import { useCourseGetRequestHook } from '../../../requestUtils';
import { IInstructor } from '../../../_models/instructor';
import RightsWrapper from '../../../rights-wrapper/rights-wrapper';
import { useState } from 'react';
import CreateNewInstructorDialog from './create-new-instructor-dialog/create-new-instructor-dialog';
import EditInstructorDialog from './edit-instructor-dialog/edit-instructor-dialog';
import DeleteInstructorDialog from './delete-instructor-dialog/delete-instructor-dialog';
import CopyInstructorDialog from './copy-instructor-dialog/copy-instructor-dialog';
import WebkuaTable from '../../../webkua-table/webkua-table';
import InstructorSkeleton from './instructor-skeleton';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface InstructorProps { }

export function Instructor(props: InstructorProps) {
  const { t } = useTranslation();
  const { data: instructors, isLoading, getData: getInstructors }: { data: IInstructor[], isLoading: boolean, getData: () => void } = useCourseGetRequestHook(`resource/instructors`, []);

  const [isNewInstructorOpen, setIsNewInstructorOpen] = useState(false);
  const [editInstructor, setEditInstructor] = useState<IInstructor | undefined>(undefined);
  const [copyInstructor, setCopyInstructor] = useState<IInstructor | undefined>(undefined);
  const [deleteInstructor, setDeleteInstructor] = useState<IInstructor | undefined>(undefined);

  const toggleNewInstructorOpen = () => setIsNewInstructorOpen(!isNewInstructorOpen);

  const openEditInstructorDialog = (e: any) => setEditInstructor(e.data);
  const closeEditInstructorDialog = () => setEditInstructor(undefined);

  const openCopyInstructorDialog = (e: any) => setCopyInstructor(e.data);
  const closeCopyInstructorDialog = () => setCopyInstructor(undefined);

  const openDeleteInstructorDialog = (e: any) => setDeleteInstructor(e.data);
  const closeDeleteInstructorDialog = () => setDeleteInstructor(undefined);

  const editRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="contained"
        size='small'
        onClick={() => openEditInstructorDialog(params)}>
        {t('standard.change')}
      </PrimaryButton>
    );
  };

  const copyRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="outlined"
        size='small'
        onClick={() => openCopyInstructorDialog(params)}>
        {t('standard.copies')}
      </PrimaryButton>
    );
  };

  const deleteRenderer = (params: ValueFormatterParams) => {
    return (
      <ErrorButton
        variant="outlined"
        size='small'
        onClick={() => openDeleteInstructorDialog(params)}>
        {t('standard.delete')}
      </ErrorButton>
    );
  };

  return (<>
    <RightsWrapper>
      <div className='mb-3'>
        <PrimaryButton
          variant="contained"
          onClick={toggleNewInstructorOpen}>
          <Icon className="me-1" fontSize="small">add</Icon>{t('standard.newInstructor')}
        </PrimaryButton>
      </div>
    </RightsWrapper>
    {isLoading ? <InstructorSkeleton /> :
      <WebkuaTable
        data={instructors}
        headers={CreateHeaders([
          textHeader("person.firstName", t('standard.firstName'), HeaderSizeEnum.LARGE),
          textHeader("person.lastName", t('standard.lastName'), HeaderSizeEnum.LARGE),
          textHeader("adskInstructorId", t('standard.adskInstructorId'), HeaderSizeEnum.LARGE),
          textHeader("person.companyName", t('standard.company'), HeaderSizeEnum.EXTRA_LARGE),
          textHeader("city", t('standard.city'), HeaderSizeEnum.EXTRA_LARGE),
          textHeader("person.email", t('standard.email'), HeaderSizeEnum.EXTRA_LARGE),
          textHeader("", "", HeaderSizeEnum.MEDIUM, {
            renderer: editRenderer,
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("", "", HeaderSizeEnum.MEDIUM, {
            renderer: copyRenderer,
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("", "", HeaderSizeEnum.MEDIUM, {
            renderer: deleteRenderer,
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          })
        ])}
      />}
    {isNewInstructorOpen && <CreateNewInstructorDialog
      onClose={toggleNewInstructorOpen}
      open={!!isNewInstructorOpen}
      refreshData={getInstructors}
    />}
    {editInstructor && <EditInstructorDialog
      onClose={closeEditInstructorDialog}
      open={!!editInstructor}
      instructorId={editInstructor.id}
      refreshData={getInstructors}
    />}
    {copyInstructor && <CopyInstructorDialog
      onClose={closeCopyInstructorDialog}
      open={!!copyInstructor}
      instructorId={copyInstructor.id}
      refreshData={getInstructors}
    />}
    {deleteInstructor && <DeleteInstructorDialog
      onClose={closeDeleteInstructorDialog}
      open={!!deleteInstructor}
      refreshData={getInstructors}
      instructorId={deleteInstructor.id}
    />}
  </>);
}

export default Instructor;
